import {
  Badge,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  Option,
  Spinner,
  Subtitle2,
  Switch,
  Textarea,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowLeftRegular,
  BoxRegular,
  DeleteRegular,
  ImageMultipleRegular,
  InfoRegular,
  MoneyRegular,
  TagRegular,
} from "@fluentui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { catalogue, type ProductDetail } from "../api/catalogue";
import { ApiError } from "../api/client";
import { commerce } from "../api/commerce";
import { ConfirmDialog } from "../shell/ConfirmDialog";
import { baeTypography } from "../theme/baeTheme";
import { Page, PageHeader } from "../ui/PageHeader";
import { Card, CardHeader } from "../ui/Surface";
import { PHONE } from "../ui/tokens";
import { AttributeField, type AttributeValue } from "./AttributeField";
import { PhotoManager } from "./PhotoManager";

const useStyles = makeStyles({
  pair: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(11rem, 1fr))",
    gap: tokens.spacingHorizontalM,
    alignItems: "start",
  },
  // One SKU: what it is, then its numbers, then whether it is offered. Reflows to a stack rather
  // than squeezing a price box to 4rem.
  variant: {
    display: "grid",
    gridTemplateColumns: "minmax(9rem, 1.4fr) repeat(2, minmax(6rem, 1fr)) auto",
    gap: tokens.spacingHorizontalM,
    alignItems: "end",
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    minWidth: 0,
    [PHONE]: { gridTemplateColumns: "1fr 1fr", rowGap: tokens.spacingVerticalS },
  },
  variantLabel: { display: "grid", gap: "1px", minWidth: 0, [PHONE]: { gridColumn: "1 / -1" } },
  wide: { gridColumn: "1 / -1" },
  name: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  sku: { fontFamily: baeTypography.fontFamilyMonospace, fontSize: "11px" },
  mono: { fontFamily: baeTypography.fontFamilyMonospace },
  muted: { color: tokens.colorNeutralForeground3 },
  stockLine: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
    minWidth: 0,
  },
  grow: { flexGrow: 1 },
  count: { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
  actions: { display: "flex", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  // The save bar follows the page down, because this form is long enough that the button would
  // otherwise be somewhere off-screen from whatever is being edited.
  sticky: {
    position: "sticky",
    bottom: 0,
    display: "flex",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
    padding: `${tokens.spacingVerticalM} 0`,
    backgroundColor: tokens.colorNeutralBackground2,
    zIndex: 1,
    [PHONE]: { bottom: "calc(3.5rem + env(safe-area-inset-bottom, 0px))" },
  },
  save: { height: "3rem" },
});

const money = (value: number | null) =>
  value === null ? "—" : `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

type VariantDraft = {
  sellingPrice: string;
  mrp: string;
  barcode: string;
  isActive: boolean;
};

/**
 * Correcting a piece after it was added.
 *
 * The add form is built to be finished in under a minute with a garment in one hand, so mistakes
 * get made — a name typed wrong, the wrong category, a price out by a zero, one photo where four
 * were wanted. This is the way back from all of it.
 *
 * Two things it will not change, and both are deliberate rather than unfinished:
 *
 *  - **The counts.** Stock moves through an append-only ledger (ADR-0005), so a miscount is
 *    corrected by an adjustment that records who changed it and why. Typing over the number would
 *    destroy the only thing anyone wants the first time a stock count disagrees with the system.
 *  - **The product code and each SKU.** They are printed on labels and held by anything the
 *    catalogue has been pushed to.
 *
 * Where the stock is *is* shown, because "I put it in the wrong store" is one of the commonest
 * corrections, and a screen that will not say where something is cannot help with it.
 */
export function EditPiecePage({ productId, onDone }: { productId: string; onDone: () => void }) {
  const styles = useStyles();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [values, setValues] = useState<Record<string, AttributeValue>>({});
  const [variantDrafts, setVariantDrafts] = useState<Record<string, VariantDraft>>({});
  const [saved, setSaved] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removed, setRemoved] = useState<string | null>(null);

  const product = useQuery({
    queryKey: ["product", productId],
    queryFn: () => catalogue.product(productId),
  });

  const categories = useQuery({ queryKey: ["categories"], queryFn: commerce.categories });

  const attributes = useQuery({
    queryKey: ["attributes", categoryId],
    queryFn: () => catalogue.attributes(categoryId || undefined),
    enabled: categoryId !== "",
  });

  // Seeded into form state once, so typing is not fighting a refetch. Keyed on the loaded product
  // so opening a different piece re-seeds rather than showing the last one's answers.
  useEffect(() => {
    const detail = product.data;
    if (!detail) return;

    setName(detail.name);
    setBrand(detail.brand ?? "");
    setShortDescription(detail.shortDescription ?? "");
    setDescription(detail.description ?? "");
    setCategoryId(detail.categoryId);
    setValues(
      Object.fromEntries(
        detail.attributes.map((entry) => [
          entry.attributeDefinitionId,
          { value: entry.value, selectedValues: entry.selectedValues },
        ]),
      ),
    );
    setVariantDrafts(
      Object.fromEntries(
        detail.variants.map((v) => [
          v.id,
          {
            sellingPrice: v.sellingPrice === null ? "" : String(v.sellingPrice),
            mrp: "",
            barcode: "",
            isActive: v.isActive,
          },
        ]),
      ),
    );
  }, [product.data]);

  const detail = product.data;
  const isSplit = (detail?.optionNames.length ?? 0) > 0;

  const selectableCategories = useMemo(
    () => (categories.data ?? []).filter((c) => c.isActive),
    [categories.data],
  );

  // Axis fields are answered per SKU, so they are not asked again about the design — asking would
  // offer one colour for a piece that comes in four.
  const questions = useMemo(
    () =>
      (attributes.data ?? [])
        .filter((a) => a.isActive && !(isSplit && a.isVariantAxis))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [attributes.data, isSplit],
  );

  const valueFor = (id: string): AttributeValue =>
    values[id] ?? { value: null, selectedValues: [] };

  const draftFor = (id: string): VariantDraft =>
    variantDrafts[id] ?? { sellingPrice: "", mrp: "", barcode: "", isActive: true };

  const touch = () => setSaved(false);

  const save = useMutation({
    mutationFn: () =>
      catalogue.updateProduct(productId, {
        name: name.trim(),
        brand: brand.trim() || null,
        shortDescription: shortDescription.trim() || null,
        description: description.trim() || null,
        categoryId,
        attributes: questions
          .map((definition) => ({
            attributeDefinitionId: definition.id,
            value: valueFor(definition.id).value,
            selectedValues: valueFor(definition.id).selectedValues,
          }))
          .filter((a) => a.value !== null || (a.selectedValues?.length ?? 0) > 0),
        variants: (detail?.variants ?? []).map((variant) => {
          const draft = draftFor(variant.id);

          return {
            id: variant.id,
            sellingPrice: draft.sellingPrice === "" ? null : Number(draft.sellingPrice),
            mrp: draft.mrp === "" ? null : Number(draft.mrp),
            barcode: draft.barcode.trim() || null,
            isActive: draft.isActive,
          };
        }),
      }),
    onSuccess: (fresh: ProductDetail) => {
      setSaved(true);
      queryClient.setQueryData(["product", productId], fresh);
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
      void queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const held = (detail?.stock ?? []).reduce((running, line) => running + line.onSiteTotal, 0);

  const remove = useMutation({
    mutationFn: () => catalogue.removeProduct(productId, held),
    onSuccess: (result) => {
      setConfirmRemove(false);
      setRemoved(result.explanation);
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
      void queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const missingRequired = questions.filter((definition) => {
    if (!definition.isRequired) return false;
    const value = valueFor(definition.id);
    return !value.value && value.selectedValues.length === 0;
  });

  const missing = [
    name.trim() === "" && "a name",
    categoryId === "" && "a category",
    ...missingRequired.map((definition) => definition.name.toLowerCase()),
  ].filter((entry): entry is string => typeof entry === "string");

  if (product.isLoading) {
    return <Spinner label="Loading the piece" />;
  }

  if (removed) {
    return (
      <Page width="narrow">
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Done</MessageBarTitle>
            {removed}
          </MessageBarBody>
        </MessageBar>
        <Button appearance="primary" onClick={onDone}>
          Back to stock
        </Button>
      </Page>
    );
  }

  if (!detail) {
    return (
      <Page width="narrow">
        <MessageBar intent="error">
          <MessageBarBody>That piece could not be loaded.</MessageBarBody>
        </MessageBar>
        <Button appearance="secondary" onClick={onDone}>
          Back to stock
        </Button>
      </Page>
    );
  }

  return (
    <Page width="medium">
      <PageHeader
        title={detail.name}
        subtitle={`${detail.categoryPath.join(" / ")} · ${detail.productCode}`}
        actions={
          <>
            <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onDone}>
              Back
            </Button>
            {!detail.isActive && (
              <Badge appearance="tint" color="warning">
                Archived
              </Badge>
            )}
          </>
        }
      />

      {save.error instanceof ApiError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not save</MessageBarTitle>
            {save.error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      {saved && !save.isPending && (
        <MessageBar intent="success">
          <MessageBarBody>Saved.</MessageBarBody>
        </MessageBar>
      )}

      <Card>
        <CardHeader
          icon={<ImageMultipleRegular />}
          title="Photos"
          hint="The front, the neckline, the work up close, the care label."
        />
        <PhotoManager productId={productId} />
      </Card>

      <Card>
        <CardHeader icon={<InfoRegular />} title="What it is" />

        <Field label="Name" required>
          <Input
            value={name}
            onChange={(_, d) => {
              touch();
              setName(d.value);
            }}
          />
        </Field>

        <div className={styles.pair}>
          <Field
            label="Category"
            required
            hint="Changing it changes which questions are asked below."
          >
            <Dropdown
              value={
                selectableCategories.find((c) => c.id === categoryId)?.path.join(" / ") ??
                detail.categoryPath.join(" / ")
              }
              selectedOptions={categoryId ? [categoryId] : []}
              onOptionSelect={(_, data) => {
                touch();
                setCategoryId(data.optionValue ?? "");
              }}
            >
              {selectableCategories.map((category) => (
                <Option key={category.id} value={category.id} text={category.path.join(" / ")}>
                  {category.path.join(" / ")}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Brand" hint="Optional.">
            <Input
              value={brand}
              onChange={(_, d) => {
                touch();
                setBrand(d.value);
              }}
            />
          </Field>
        </div>

        <Field label="One-line summary" hint="Optional. Shown in lists and on sales channels.">
          <Input
            value={shortDescription}
            placeholder="Ivory mul cotton kurti with hand Chikankari"
            onChange={(_, d) => {
              touch();
              setShortDescription(d.value);
            }}
          />
        </Field>

        <Field label="Description" hint="Optional. The longer note.">
          <Textarea
            value={description}
            rows={3}
            onChange={(_, d) => {
              touch();
              setDescription(d.value);
            }}
          />
        </Field>
      </Card>

      <Card>
        <CardHeader
          icon={<TagRegular />}
          title="Details"
          hint={
            isSplit
              ? `${detail.optionNames.join(" and ")} are answered per SKU below, so they are not asked here.`
              : undefined
          }
        />

        {attributes.isLoading ? (
          <Spinner size="tiny" label="Loading fields" />
        ) : questions.length === 0 ? (
          <Caption1 className={styles.muted}>No extra fields for this category.</Caption1>
        ) : (
          questions.map((definition) => (
            <AttributeField
              key={definition.id}
              definition={definition}
              value={valueFor(definition.id)}
              onChange={(next) => {
                touch();
                setValues((current) => ({ ...current, [definition.id]: next }));
              }}
            />
          ))
        )}
      </Card>

      <Card>
        <CardHeader
          icon={<MoneyRegular />}
          title={detail.variants.length > 1 ? `${detail.variants.length} SKUs` : "Price and code"}
          hint="MRP is what the tag says. A selling price above it is refused — that is the law in India, not a preference."
        />

        {detail.variants.map((variant) => {
          const draft = draftFor(variant.id);

          return (
            <div key={variant.id} className={styles.variant}>
              <div className={styles.variantLabel}>
                <Subtitle2 className={styles.name}>
                  {variant.optionSummary || detail.name}
                </Subtitle2>
                <span className={`${styles.sku} ${styles.muted}`}>
                  {variant.sku} · {variant.onHand} on hand · cost {money(variant.costPrice)}
                </span>
              </div>

              <Field label="Selling price">
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={draft.sellingPrice}
                  onChange={(_, d) => {
                    touch();
                    setVariantDrafts((current) => ({
                      ...current,
                      [variant.id]: { ...draft, sellingPrice: d.value },
                    }));
                  }}
                />
              </Field>

              <Field label="MRP" hint="Blank matches the price.">
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={draft.mrp}
                  onChange={(_, d) => {
                    touch();
                    setVariantDrafts((current) => ({
                      ...current,
                      [variant.id]: { ...draft, mrp: d.value },
                    }));
                  }}
                />
              </Field>

              <Field label="Offered">
                <Switch
                  checked={draft.isActive}
                  onChange={(_, d) => {
                    touch();
                    setVariantDrafts((current) => ({
                      ...current,
                      [variant.id]: { ...draft, isActive: d.checked },
                    }));
                  }}
                />
              </Field>

              <Field
                className={styles.wide}
                label="Retail barcode"
                hint="EAN-13 or UPC from the supplier. Not the serial — that is per piece."
              >
                <Input
                  className={styles.mono}
                  value={draft.barcode}
                  placeholder="Leave blank if there is none"
                  onChange={(_, d) => {
                    touch();
                    setVariantDrafts((current) => ({
                      ...current,
                      [variant.id]: { ...draft, barcode: d.value },
                    }));
                  }}
                />
              </Field>
            </div>
          );
        })}
      </Card>

      <Card>
        <CardHeader
          icon={<BoxRegular />}
          title="Where the stock is"
          hint="Counts come from the stock ledger. To move a piece, use Find a unit; to write stock off, remove the piece below."
        />

        {detail.stock.length === 0 ? (
          <Caption1 className={styles.muted}>None in stock anywhere.</Caption1>
        ) : (
          detail.stock.map((line) => (
            <div key={`${line.productVariantId}-${line.siteId}`} className={styles.stockLine}>
              <Badge appearance="outline" size="small">
                {line.siteCode}
              </Badge>
              <span className={styles.name}>{line.siteName}</span>
              <span className={styles.grow} />
              <Caption1>
                <span className={styles.count}>{line.available}</span> available of{" "}
                <span className={styles.count}>{line.onSiteTotal}</span>
              </Caption1>
              <Caption1 className={`${styles.sku} ${styles.muted}`}>{line.sku}</Caption1>
            </div>
          ))
        )}
      </Card>

      {missing.length > 0 && (
        <Caption1 className={styles.muted}>Still needed: {missing.join(", ")}.</Caption1>
      )}

      <div className={styles.sticky}>
        <Button
          appearance="primary"
          size="large"
          className={styles.save}
          disabled={missing.length > 0 || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? <Spinner size="tiny" /> : "Save changes"}
        </Button>
        <Button appearance="subtle" size="large" onClick={onDone}>
          Done
        </Button>
      </div>

      <Card quiet>
        <CardHeader
          icon={<DeleteRegular />}
          title="Remove this piece"
          hint={
            held > 0
              ? `${held} piece(s) are in stock. Removing writes them off through the ledger, so the loss is recorded rather than silently vanishing.`
              : "Nothing is in stock, so there is nothing to write off."
          }
        />
        <div className={styles.actions}>
          <Button
            appearance="secondary"
            icon={<DeleteRegular />}
            onClick={() => {
              remove.reset();
              setConfirmRemove(true);
            }}
          >
            Remove
          </Button>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmRemove}
        title={`Remove ${detail.name}?`}
        confirmLabel={held > 0 ? `Write off ${held} and remove` : "Remove it"}
        busy={remove.isPending}
        error={remove.error}
        onCancel={() => setConfirmRemove(false)}
        onConfirm={() => remove.mutate()}
      >
        <Caption1>
          {held > 0
            ? `${held} piece(s) will be written off — recorded in the stock ledger as a loss, dated today. `
            : ""}
          If this piece has ever been received it is archived rather than deleted, so the delivery
          note that names it keeps working. Otherwise it goes entirely.
        </Caption1>
      </ConfirmDialog>
    </Page>
  );
}
